import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  CreditCard,
  Landmark,
  Users,
  CalendarDays,
  Scissors,
  Ticket,
  Gift,
  Settings,
  Leaf,
  MapPin,
  Link2,
  Bell,
  ClipboardCheck,
  BookOpen,
  Menu,
  X,
  LogOut,
  Activity,
  Stethoscope,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';
import { ScrollArea } from '@/components/ui/scroll-area';

/* Nav grouped by what the work actually is — the grouping encodes how the ops
   team thinks about the platform (run it, stock it, grow it, configure it),
   which reads far better than one flat list of thirteen links. */
interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
}

const navGroups: { title: string; items: NavItem[] }[] = [
  {
    title: 'Overview',
    items: [{ to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true }],
  },
  {
    title: 'Operations',
    items: [
      { to: '/admin/bookings', label: 'Bookings', icon: CalendarDays },
      { to: '/admin/payments', label: 'Payments', icon: CreditCard },
      { to: '/admin/payouts', label: 'Clinic Payouts', icon: Landmark },
      { to: '/admin/customers', label: 'Customers', icon: Users },
      { to: '/admin/centres', label: 'Clinics', icon: MapPin },
      { to: '/admin/consultants', label: 'Consultants', icon: Stethoscope },
      { to: '/admin/centre-approvals', label: 'Approvals', icon: ClipboardCheck },
    ],
  },
  {
    title: 'Catalog',
    items: [
      { to: '/admin/services', label: 'Services', icon: Scissors },
      { to: '/admin/therapy-options', label: 'Therapy Options', icon: Leaf },
      { to: '/admin/articles', label: 'Knowledge', icon: BookOpen },
    ],
  },
  {
    title: 'Growth',
    items: [
      { to: '/admin/coupons', label: 'Coupons', icon: Ticket },
      { to: '/admin/listing-promos', label: 'Partner Promos', icon: Gift },
      { to: '/admin/marketing', label: 'Marketing', icon: Link2 },
      { to: '/admin/notifications', label: 'Notifications', icon: Bell },
    ],
  },
  {
    title: 'System',
    items: [{ to: '/admin/site-config', label: 'Site Config', icon: Settings }],
  },
];

const INK = '#0D2137'; // brand navy — the console rail
const MINT = '#2DD4BF'; // brand teal, brightened to read on navy

function Brand({ onClose }: { onClose?: () => void }) {
  return (
    <div className="flex h-16 shrink-0 items-center justify-between px-5">
      <div className="flex items-center gap-2.5">
        <span
          className="flex size-8 items-center justify-center rounded-lg"
          style={{ backgroundColor: 'rgba(45,212,191,0.14)', color: MINT }}
        >
          <Activity className="size-4" />
        </span>
        <div className="leading-none">
          <span className="block text-[15px] font-extrabold tracking-tight text-white">recovry</span>
          <span className="mt-1 block text-[10px] font-semibold uppercase tracking-[0.2em] text-white/40">
            Admin
          </span>
        </div>
      </div>
      {onClose && (
        <button
          onClick={onClose}
          className="rounded-md p-1.5 text-white/60 transition-colors hover:bg-white/10 hover:text-white"
          aria-label="Close menu"
        >
          <X className="size-5" />
        </button>
      )}
    </div>
  );
}

function Sidebar({ onClose }: { onClose?: () => void }) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    signOut();
    navigate('/');
  };

  return (
    <div className="flex h-full flex-col" style={{ backgroundColor: INK }}>
      <Brand onClose={onClose} />

      <ScrollArea className="min-h-0 flex-1 px-3">
        <nav className="space-y-5 pb-4">
          {navGroups.map((group) => (
            <div key={group.title}>
              <p className="mb-1.5 px-3 text-[10px] font-semibold uppercase tracking-[0.14em] text-white/35">
                {group.title}
              </p>
              <div className="space-y-0.5">
                {group.items.map(({ to, label, icon: Icon, end }) => (
                  <NavLink
                    key={to}
                    to={to}
                    end={end}
                    onClick={onClose}
                    className={({ isActive }) =>
                      cn(
                        'group relative flex items-center gap-3 rounded-lg px-3 py-2 text-[13.5px] font-medium transition-colors',
                        isActive
                          ? 'bg-white/10 text-white'
                          : 'text-white/60 hover:bg-white/[0.06] hover:text-white',
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <span
                          className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full transition-opacity"
                          style={{ backgroundColor: MINT, opacity: isActive ? 1 : 0 }}
                        />
                        <Icon
                          className="size-[17px] shrink-0 transition-colors"
                          style={{ color: isActive ? MINT : undefined }}
                        />
                        {label}
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
      </ScrollArea>

      {/* Identity + sign out */}
      <div className="shrink-0 border-t border-white/10 p-3">
        <div className="mb-1.5 flex items-center gap-2.5 px-2">
          <span
            className="flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-bold"
            style={{ backgroundColor: 'rgba(45,212,191,0.18)', color: MINT }}
          >
            {(user?.email?.[0] ?? 'A').toUpperCase()}
          </span>
          <div className="min-w-0 leading-tight">
            <p className="truncate text-xs font-semibold text-white">Administrator</p>
            <p className="truncate text-[11px] text-white/40">{user?.email ?? ''}</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-[13px] font-medium text-white/60 transition-colors hover:bg-red-500/15 hover:text-red-300"
        >
          <LogOut className="size-4" /> Log out
        </button>
      </div>
    </div>
  );
}

export function AdminLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="flex h-screen overflow-hidden bg-[#F5F7FA] dark:bg-background">
      {/* Desktop rail */}
      <aside className="hidden w-64 shrink-0 lg:block">
        <Sidebar />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-[1px]" onClick={() => setMobileOpen(false)} />
          <aside className="relative h-full w-64">
            <Sidebar onClose={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Mobile top bar */}
        <div className="flex h-14 items-center gap-3 border-b bg-card px-4 lg:hidden">
          <button
            onClick={() => setMobileOpen(true)}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Open menu"
          >
            <Menu className="size-5" />
          </button>
          <div className="flex items-center gap-2">
            <span
              className="flex size-6 items-center justify-center rounded-md"
              style={{ backgroundColor: 'rgba(13,33,55,0.08)', color: INK }}
            >
              <Activity className="size-3.5" />
            </span>
            <span className="font-bold tracking-tight text-foreground">recovry Admin</span>
          </div>
        </div>

        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
